import type { FC } from 'react';
import { useState } from 'react';
import { Button, Form, type FormProps, Input, message, Modal } from 'antd';
import { useTranslation } from 'react-i18next';
import { useCreateFolder } from '@/hooks/useFolders';

type FieldType = {
  filename?: string;
};

interface AddModalProps {
  node: any;
  open: boolean;
  onOk: () => void;
  onCancel: () => void;
  onLoadData: (currentNode: any, newNode: any) => void;
}

const ModalAddFolder: FC<AddModalProps> = ({
  node,
  onLoadData,
  open,
  onOk,
  onCancel,
}) => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const createFolderMutation = useCreateFolder();
  const [isRefetching, setIsRefetching] = useState(false);

  const onFinish: FormProps<FieldType>['onFinish'] = async values => {
    try {
      const payload: any = {
        folder_name: (values.filename || '').trim(),
      };
      if (node?.id) {
        payload['parent_id'] = node?.id;
      }
      const { success, data, error } =
        await createFolderMutation.mutateAsync(payload);
      if (!success) {
        message.error(error || t('home.documents.modals.addFolder.error'));
        return;
      }
      message.success(t('home.documents.modals.addFolder.success'));
      form.resetFields();
      setIsRefetching(true);
      try {
        await onLoadData(node, data);
      } finally {
        setIsRefetching(false);
      }
      onCancel();
    } catch (err) {
      message.error(t('home.documents.modals.addFolder.error'));
    }
  };

  return (
    <div>
      <Modal
        title={t('home.documents.modals.addFolder.title')}
        open={open}
        destroyOnClose
        footer={
          <Button
            type="primary"
            onClick={() => form.submit()}
            loading={createFolderMutation.isPending || isRefetching}
          >
            {t('home.documents.modals.addFolder.submit')}
          </Button>
        }
        onOk={onOk}
        onCancel={() => {
          if (!isRefetching) {
            form.resetFields();
            onCancel();
          }
        }}
        closable={!isRefetching}
        maskClosable={!isRefetching}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          autoComplete="off"
        >
          <Form.Item
            name="filename"
            label={t('home.documents.modals.addFolder.label')}
            rules={[
              {
                required: true,
                whitespace: true,
                message: t('home.documents.modals.addFolder.requiredMessage'),
              },
              {
                pattern: /^[^\/\\:\*\?"<>\|]+$/,
                message: t('home.documents.modals.addFolder.invalid'),
              },
            ]}
          >
            <Input
              placeholder={t('home.documents.modals.addFolder.placeholder')}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ModalAddFolder;
